/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EditIcon from '@atlaskit/icon/core/edit';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { Space } from '../../controllers/space-store/types';

import messages from './messages';

const styles = cssMap({
	rowActions: {
		flexShrink: 0,
		width: '32px',
		display: 'flex',
		justifyContent: 'center',
	},
});

export type SpaceRowInteractionProps = {
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	onFocus: () => void;
	onBlur: (event: React.FocusEvent) => void;
};

type SpaceRowActionsProps = {
	space: Space;
	onRename: () => void;
	onDeleteSpace: (space: Space) => void;
	children: (props: {
		rowInteractionProps: SpaceRowInteractionProps;
		actions: React.JSX.Element;
	}) => React.JSX.Element;
};

export const SpaceRowActions = ({
	space,
	onRename,
	onDeleteSpace,
	children,
}: SpaceRowActionsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
	const [isActionVisible, setIsActionVisible] = useState(false);

	const showActions = useCallback(() => {
		setIsActionVisible(true);
	}, []);

	const hideActionsIfMenuClosed = useCallback(() => {
		if (!isMoreMenuOpen) {
			setIsActionVisible(false);
		}
	}, [isMoreMenuOpen]);

	const handleBlur = useCallback((event: React.FocusEvent) => {
		if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
			return;
		}
		setIsActionVisible(false);
	}, []);

	const handleOpenChange = useCallback(({ isOpen }: { isOpen: boolean }) => {
		setIsMoreMenuOpen(isOpen);
	}, []);

	const handleMoreButtonClick = useCallback((event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		setIsMoreMenuOpen((currentIsOpen) => !currentIsOpen);
	}, []);

	const handleRenameClick = useCallback(() => {
		setIsMoreMenuOpen(false);
		onRename();
	}, [onRename]);

	const handleDeleteClick = useCallback(() => {
		setIsMoreMenuOpen(false);
		onDeleteSpace(space);
	}, [onDeleteSpace, space]);

	const rowInteractionProps = useMemo(
		() => ({
			onMouseEnter: showActions,
			onMouseLeave: hideActionsIfMenuClosed,
			onFocus: showActions,
			onBlur: handleBlur,
		}),
		[handleBlur, hideActionsIfMenuClosed, showActions],
	);

	const shouldShowActions = isActionVisible || isMoreMenuOpen;

	return children({
		rowInteractionProps,
		actions: (
			<Box xcss={styles.rowActions}>
				{shouldShowActions ? (
					<DropdownMenu
						isOpen={isMoreMenuOpen}
						placement="bottom-end"
						onOpenChange={handleOpenChange}
						label={formatMessage(messages.moreMenuLabel)}
						trigger={({ triggerRef, ...triggerProps }) => (
							<IconButton
								{...triggerProps}
								ref={triggerRef}
								testId={`browse-spaces-fullscreen-row-more-${space.id}`}
								icon={(iconProps) => <MoreIcon {...iconProps} size="small" />}
								appearance="subtle"
								spacing="compact"
								onClick={handleMoreButtonClick}
								label={formatMessage(messages.moreMenuLabel)}
							/>
						)}
					>
						<DropdownItemGroup>
							<DropdownItem elemBefore={<EditIcon label="" />} onClick={handleRenameClick}>
								{formatMessage(messages.moreMenuRenameLabel)}
							</DropdownItem>
							<DropdownItem
								elemBefore={<DeleteIcon label="" color={token('color.icon.danger')} />}
								onClick={handleDeleteClick}
							>
								{formatMessage(messages.moreMenuDeleteLabel)}
							</DropdownItem>
						</DropdownItemGroup>
					</DropdownMenu>
				) : null}
			</Box>
		),
	});
};
